/**
 * Testimonials admin page (testimonials plugin).
 *
 * Route: /projects/:projectId/testimonials
 *
 *   - "Copy invite link" mints a personal, expiring link to the site's own
 *     /share-your-story page and puts it on the clipboard. The link is shown
 *     once: the server keeps only its hash.
 *   - Submissions arrive Pending; approve / reject / feature / reorder them.
 *   - The customer's words, name and rating are NOT editable here, by design.
 *     What the site shows is exactly what the customer sent.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  Link2,
  Mail,
  MessageCircle,
  Quote,
  Settings2,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers, type Testimonial, type TestimonialInvite } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { DataState } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm-provider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type View = 'testimonials' | 'links';

const STATUS_BADGE: Record<Testimonial['status'], { label: string; className: string }> = {
  pending: { label: 'Pending review', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Live on site', className: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
};

const INVITE_BADGE: Record<TestimonialInvite['state'], { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-800' },
  used: { label: 'Used', className: 'bg-slate-100 text-slate-700' },
  expired: { label: 'Expired', className: 'bg-slate-100 text-slate-500' },
  revoked: { label: 'Revoked', className: 'bg-red-100 text-red-700' },
};

const NAME_PREF_LABEL: Record<Testimonial['namePreference'], string> = {
  full: 'full name',
  'first-initial': 'first name + initial',
  initials: 'initials',
  anonymous: 'no name',
};

const fmtDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function TestimonialsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [view, setView] = useState<View>('testimonials');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [customOpen, setCustomOpen] = useState(false);
  const [draft, setDraft] = useState({ recipientName: '', label: '', days: '14', uses: '1' });
  const [lastLink, setLastLink] = useState<{ url: string; recipientName?: string } | null>(null);

  const listQuery = useQuery({
    queryKey: qk.testimonialList(projectId, statusFilter),
    queryFn: async () => {
      const res = await apiHelpers.listTestimonials(projectId!, {
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        limit: 100,
      });
      return (res.data.data || []) as Testimonial[];
    },
    enabled: Boolean(projectId),
  });

  const statsQuery = useQuery({
    queryKey: qk.testimonialStats(projectId),
    queryFn: async () => {
      const res = await apiHelpers.getTestimonialStats(projectId!);
      return res.data.data || { pending: 0, approved: 0, rejected: 0 };
    },
    enabled: Boolean(projectId),
  });

  const invitesQuery = useQuery({
    queryKey: qk.testimonialInvites(projectId),
    queryFn: async () => {
      const res = await apiHelpers.listTestimonialInvites(projectId!);
      return (res.data.data || []) as TestimonialInvite[];
    },
    enabled: Boolean(projectId) && view === 'links',
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.testimonials(projectId) });

  const createInvite = useMutation({
    mutationFn: async (data: {
      recipientName?: string;
      label?: string;
      expiresInDays?: number;
      maxUses?: number;
    }) => (await apiHelpers.createTestimonialInvite(projectId!, data)).data.data,
    onSuccess: async (invite, vars) => {
      invalidate();
      if (!invite) return;
      setLastLink({ url: invite.url, recipientName: vars.recipientName });
      setCustomOpen(false);
      setDraft({ recipientName: '', label: '', days: '14', uses: '1' });
      const copied = await copyText(invite.url);
      if (!invite.enabled) {
        toast.warning(
          'Link created, but the Testimonials plugin is disabled, so the link will not open until you enable it.'
        );
      } else {
        toast.success(copied ? 'Invite link copied' : 'Invite link created');
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not create the link')),
  });

  const revokeInvite = useMutation({
    mutationFn: (id: string) => apiHelpers.revokeTestimonialInvite(projectId!, id),
    onSuccess: () => {
      invalidate();
      toast.success('Link revoked');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not revoke the link')),
  });

  const moderate = useMutation({
    mutationFn: (args: {
      id: string;
      data: Partial<{ status: Testimonial['status']; featured: boolean }>;
    }) => apiHelpers.moderateTestimonial(projectId!, args.id, args.data),
    onSuccess: (_r, args) => {
      invalidate();
      if (args.data.status === 'approved') toast.success('Published on your site');
      else if (args.data.status === 'rejected') toast.success('Rejected');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Update failed')),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => apiHelpers.reorderTestimonials(projectId!, ids),
    onSuccess: invalidate,
    onError: (err) => toast.error(apiErrorMessage(err, 'Reorder failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiHelpers.deleteTestimonial(projectId!, id),
    onSuccess: () => {
      invalidate();
      toast.success('Deleted');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Delete failed')),
  });

  const move = (items: Testimonial[], index: number, dir: -1 | 1) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(index + dir, 0, item!);
    reorder.mutate(next.map((t) => t._id));
  };

  const stats = statsQuery.data;
  const filters: Array<{ id: StatusFilter; label: string; count?: number }> = [
    { id: 'pending', label: 'Pending', count: stats?.pending },
    { id: 'approved', label: 'Live', count: stats?.approved },
    { id: 'rejected', label: 'Rejected', count: stats?.rejected },
    { id: 'all', label: 'All' },
  ];

  const shareText = (url: string, name?: string) =>
    `${name ? `Hi ${name}, ` : ''}thank you for choosing us. If you have a minute, we would love to hear about your experience in your own words: ${url}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Quote className="h-6 w-6" /> Testimonials
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            Send a customer a personal link and they write the testimonial themselves, on your site.
            Nothing appears until you approve it, and it is always published word for word as they
            wrote it.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => createInvite.mutate({})}
            disabled={createInvite.isPending}
            title="Creates a single-use link that expires in 14 days and copies it"
          >
            <Link2 className="mr-2 h-4 w-4" /> Copy invite link
          </Button>
          <Button variant="outline" onClick={() => setCustomOpen(true)} title="Name, expiry, uses">
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {lastLink && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="space-y-3 py-4">
            <p className="text-sm font-medium text-emerald-900">
              New invite link{lastLink.recipientName ? ` for ${lastLink.recipientName}` : ''}. Copy
              it now: for security it is not shown again.
            </p>
            <div className="flex gap-2">
              <Input readOnly value={lastLink.url} className="bg-white font-mono text-xs" />
              <Button
                variant="outline"
                onClick={async () => {
                  if (await copyText(lastLink.url)) toast.success('Copied');
                  else toast.error('Copy failed, select the text and copy it');
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareText(lastLink.url, lastLink.recipientName))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="mr-1.5 h-4 w-4" /> Send on WhatsApp
                </a>
              </Button>
              <Button asChild size="sm" variant="outline">
                <a
                  href={`mailto:?subject=${encodeURIComponent('Would you share your experience?')}&body=${encodeURIComponent(shareText(lastLink.url, lastLink.recipientName))}`}
                >
                  <Mail className="mr-1.5 h-4 w-4" /> Send by email
                </a>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setLastLink(null)}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 border-b">
        {(['testimonials', 'links'] as View[]).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              view === v
                ? 'border-primary text-foreground'
                : 'text-muted-foreground border-transparent'
            }`}
          >
            {v === 'testimonials' ? 'Testimonials' : 'Invite links'}
          </button>
        ))}
      </div>

      {view === 'testimonials' && (
        <>
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <Button
                key={f.id}
                size="sm"
                variant={statusFilter === f.id ? 'default' : 'outline'}
                onClick={() => setStatusFilter(f.id)}
              >
                {f.label}
                {typeof f.count === 'number' && (
                  <span className="ml-1.5 rounded-full bg-black/10 px-1.5 text-xs">{f.count}</span>
                )}
              </Button>
            ))}
          </div>

          <DataState query={listQuery}>
            {(items) =>
              items.length === 0 ? (
                <Card>
                  <CardContent className="text-muted-foreground py-12 text-center text-sm">
                    {statusFilter === 'pending'
                      ? 'Nothing waiting for review. Copy an invite link and send it to a happy customer.'
                      : 'No testimonials here yet.'}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {items.map((t, index) => (
                    <Card key={t._id}>
                      <CardContent className="space-y-3 py-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge className={STATUS_BADGE[t.status].className}>
                            {STATUS_BADGE[t.status].label}
                          </Badge>
                          {t.featured && (
                            <Badge className="bg-indigo-100 text-indigo-800">Featured</Badge>
                          )}
                          {typeof t.rating === 'number' && (
                            <Badge variant="outline">{t.rating} / 5</Badge>
                          )}
                          <span className="text-muted-foreground text-xs">
                            Sent {fmtDate(t.submittedAt)}
                          </span>
                        </div>
                        <blockquote className="whitespace-pre-wrap border-l-4 pl-4 text-[15px] leading-relaxed">
                          {t.body}
                        </blockquote>
                        <p className="text-sm">
                          <span className="font-medium">{t.displayName}</span>
                          {t.context && (
                            <span className="text-muted-foreground"> · {t.context}</span>
                          )}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Private: submitted as “{t.authorName}”, chose to show{' '}
                          {NAME_PREF_LABEL[t.namePreference]}. Consent recorded{' '}
                          {fmtDate(t.consent?.at)}.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {t.status !== 'approved' && (
                            <Button
                              size="sm"
                              onClick={() =>
                                moderate.mutate({ id: t._id, data: { status: 'approved' } })
                              }
                            >
                              <Check className="mr-1 h-3.5 w-3.5" /> Approve
                            </Button>
                          )}
                          {t.status !== 'rejected' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                moderate.mutate({ id: t._id, data: { status: 'rejected' } })
                              }
                            >
                              <X className="mr-1 h-3.5 w-3.5" />
                              {t.status === 'approved' ? 'Take down' : 'Reject'}
                            </Button>
                          )}
                          {t.status === 'approved' && (
                            <Button
                              size="sm"
                              variant={t.featured ? 'default' : 'outline'}
                              onClick={() =>
                                moderate.mutate({ id: t._id, data: { featured: !t.featured } })
                              }
                              title="Featured testimonials are shown first"
                            >
                              <Star className="mr-1 h-3.5 w-3.5" />{' '}
                              {t.featured ? 'Featured' : 'Feature'}
                            </Button>
                          )}
                          {statusFilter === 'approved' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={index === 0}
                                onClick={() => move(items, index, -1)}
                              >
                                <ArrowUp className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={index === items.length - 1}
                                onClick={() => move(items, index, 1)}
                              >
                                <ArrowDown className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={async () => {
                              if (
                                await confirm({
                                  title: 'Delete this testimonial?',
                                  message:
                                    'It is removed for good, including from the site. Use this when the customer asks you to delete it.',
                                  danger: true,
                                })
                              ) {
                                remove.mutate(t._id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <p className="text-muted-foreground text-xs">
                    Words cannot be edited. If something needs changing, reject it and send the
                    customer a new link so they can rewrite it themselves.
                  </p>
                </div>
              )
            }
          </DataState>
        </>
      )}

      {view === 'links' && (
        <DataState query={invitesQuery}>
          {(invites) =>
            invites.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-12 text-center text-sm">
                  No invite links yet.
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="divide-y p-0">
                  {invites.map((i) => (
                    <div key={i._id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <Badge className={INVITE_BADGE[i.state].className}>
                        {INVITE_BADGE[i.state].label}
                      </Badge>
                      <div className="min-w-0 flex-1 text-sm">
                        <p className="font-medium">
                          {i.label || i.recipientName || 'Unnamed link'}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Created {fmtDate(i.createdAt)} · used {i.uses}/{i.maxUses} · expires{' '}
                          {fmtDate(i.expiresAt)}
                        </p>
                      </div>
                      {i.state === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => revokeInvite.mutate(i._id)}
                          disabled={revokeInvite.isPending}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )
          }
        </DataState>
      )}

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New invite link</DialogTitle>
            <DialogDescription>
              The customer sees their first name in the greeting. The label is only for you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ti-name">Customer first name (optional)</Label>
              <Input
                id="ti-name"
                value={draft.recipientName}
                onChange={(e) => setDraft({ ...draft, recipientName: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ti-label">Private label (optional)</Label>
              <Input
                id="ti-label"
                placeholder="For example: Priya, knee rehab, October"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ti-days">Expires after (days)</Label>
                <Input
                  id="ti-days"
                  type="number"
                  min={1}
                  max={60}
                  value={draft.days}
                  onChange={(e) => setDraft({ ...draft, days: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ti-uses">Testimonials allowed</Label>
                <Input
                  id="ti-uses"
                  type="number"
                  min={1}
                  max={50}
                  value={draft.uses}
                  onChange={(e) => setDraft({ ...draft, uses: e.target.value })}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Keep it at 1 for a personal link. A higher number lets one link collect several
              testimonials, for example a QR code at the front desk.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={createInvite.isPending}
              onClick={() =>
                createInvite.mutate({
                  recipientName: draft.recipientName.trim() || undefined,
                  label: draft.label.trim() || undefined,
                  expiresInDays: Math.min(60, Math.max(1, Number(draft.days) || 14)),
                  maxUses: Math.min(50, Math.max(1, Number(draft.uses) || 1)),
                })
              }
            >
              <Link2 className="mr-2 h-4 w-4" /> Create and copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
